import React, { useEffect, useState } from 'react';
import { FactCheck } from '../data/mockData';
import { AlertCircleIcon, CheckCircleIcon, AlertTriangleIcon, HelpCircleIcon } from 'lucide-react';
interface FactCheckPanelProps {
  factChecks: FactCheck[];
  currentTime: number;
}
export const FactCheckPanel: React.FC<FactCheckPanelProps> = ({
  factChecks,
  currentTime
}) => {
  const [visibleFactChecks, setVisibleFactChecks] = useState<FactCheck[]>([]);
  const [newFactCheck, setNewFactCheck] = useState<FactCheck | null>(null);
  // Display fact checks that are within 30 seconds of the current time
  useEffect(() => {
    const relevantChecks = factChecks.filter(check => {
      // Show fact checks that are within 30 seconds of their timestamp
      return currentTime >= check.timestamp && currentTime <= check.timestamp + 30;
    });
    setVisibleFactChecks(relevantChecks);
    // Check if there's a new fact check to highlight
    if (relevantChecks.length > 0) {
      const latestCheck = relevantChecks.reduce((latest, check) => {
        return Math.abs(check.timestamp - currentTime) < Math.abs(latest.timestamp - currentTime) ? check : latest;
      }, relevantChecks[0]);
      if (Math.abs(latestCheck.timestamp - currentTime) < 1) {
        setNewFactCheck(latestCheck);
        setTimeout(() => setNewFactCheck(null), 3000);
      }
    }
  }, [currentTime, factChecks]);
  // Get icon based on rating
  const getRatingIcon = (rating: string) => {
    switch (rating.toLowerCase()) {
      case 'true':
        return <CheckCircleIcon className="text-green-500" size={20} data-id="element-109" />;
      case 'false':
        return <AlertCircleIcon className="text-red-500" size={20} data-id="element-110" />;
      case 'misleading':
        return <AlertTriangleIcon className="text-yellow-500" size={20} data-id="element-111" />;
      default:
        return <HelpCircleIcon className="text-gray-500" size={20} data-id="element-112" />;
    }
  };
  // Get background color based on rating
  const getRatingClass = (rating: string): string => {
    switch (rating.toLowerCase()) {
      case 'true':
        return 'bg-green-50 border-green-200';
      case 'false':
        return 'bg-red-50 border-red-200';
      case 'misleading':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };
  if (visibleFactChecks.length === 0) {
    return <div className="p-4 bg-gray-50 rounded-md text-center text-gray-500" data-id="element-113">
        No fact checks at this moment in the debate
      </div>;
  }
  return <div data-id="element-114">
      <h3 className="text-lg font-medium mb-3" data-id="element-115">Fact Checks</h3>
      <div className="space-y-3" data-id="element-116">
        {visibleFactChecks.map((check, index) => <div key={index} className={`p-4 border rounded-md transition-all ${getRatingClass(check.rating)} ${newFactCheck === check ? 'scale-105 shadow-md' : ''}`} data-id="element-117">
            <div className="flex items-start" data-id="element-118">
              <div className="mr-3 pt-0.5" data-id="element-119">{getRatingIcon(check.rating)}</div>
              <div className="flex-1" data-id="element-120">
                <div className="flex justify-between mb-1" data-id="element-121">
                  <span className="font-medium" data-id="element-122">{check.speaker}</span>
                  <span className="text-sm text-gray-500" data-id="element-123">
                    {Math.floor(check.timestamp / 60)}:
                    {(check.timestamp % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-124">"{check.claim}"</p>
                <div className="mb-1" data-id="element-125">
                  <span className={`inline-block px-2 py-0.5 text-sm rounded-full font-medium ${check.rating.toLowerCase() === 'true' ? 'bg-green-100 text-green-800' : check.rating.toLowerCase() === 'false' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-126">
                    {check.rating}
                  </span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-127">{check.explanation}</p>
                {check.source && <p className="text-xs text-gray-500 mt-2" data-id="element-128">
                    Source: {check.source}
                  </p>}
              </div>
            </div>
          </div>)}
      </div>
    </div>;
};